import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-banner-image',
  templateUrl: './banner-image.component.html',
  styleUrls: ['./banner-image.component.scss']
})
export class BannerImageComponent implements OnInit {
  bannerForm: FormGroup;
  showBannerModal = false;
  showViewModal = false;
  isLoadingViewModal = false;
  banners: any[] = [];
  selectedBanner: any = null;
  destroy$ = new Subject<void>();
  bannerImages: { [key: number]: SafeUrl } = {};

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private cdRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.bannerForm = this.fb.group({
      bannerTitle: [''],
      location: [''],
      startDate: [''],
      endDate: [''],
      startTime: [''],
      endTime: [''],
      bannerImagePath: [''],
      shortDescription: [''],
      description: [''],
      fileData: [''],
      fileType: ['']
    });
  }

  ngOnInit(): void {
    this.loadBanners();
  }

  openBannerModal(): void {
    this.showBannerModal = true;
  }

  closeBannerModal(): void {
    this.showBannerModal = false;
    this.bannerForm.reset();
  }

  onFileChange(event: any) {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => {
    const base64 = (reader.result as string).split(',')[1];

    // Extract the extension from MIME type (image/jpeg → jpeg)
    let fileType = file.type.split('/')[1].toLowerCase();

    // Normalize "jpeg" to "jpg"
    if (fileType === 'jpeg') {
      fileType = 'jpg';
    }

    this.bannerForm.patchValue({
      fileData: base64,
      fileType: fileType, // ✅ now just "jpg", "png", etc.
    });

    console.log('File loaded:', this.bannerForm.value);
  };

  reader.readAsDataURL(file);
} 

  onSubmit(): void {
    if (this.bannerForm.valid) {
      if (this.bannerForm.get('bannerDetailsSeq')) {
        this.updateBannerDetails();
      } else {
        this.saveBannerDetails();
      }
    }
  }

  viewBanner(bannerDetailsSeq: number): void {
    const banner = this.banners.find(b => b.bannerDetailsSeq === bannerDetailsSeq);
    if (banner) {
      console.log('Viewing banner:', banner);
      this.selectedBanner = banner;
      this.isLoadingViewModal = true;
      this.showViewModal = true;
      this.loadBannerImageForView(bannerDetailsSeq);
    } else {
      this.toastr.error('Banner not found');
    }
  }

  loadBannerImageForView(bannerDetailsSeq: number): void {
    this.appService.getBannerImageById(bannerDetailsSeq)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          this.bannerImages[bannerDetailsSeq] = this.sanitizer.bypassSecurityTrustUrl(url);
          this.isLoadingViewModal = false;
          this.cdRef.detectChanges();
        },
        error: (err) => {
          console.error(`Error loading banner image ${bannerDetailsSeq}:`, err);
          this.isLoadingViewModal = false;
        }
      });
  }

loadBannerImage(bannerDetailsSeq: number): void {
  this.isLoadingViewModal = true;

  this.appService.getBannerImageById(bannerDetailsSeq)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (blob: Blob) => {
        // ✅ Ensure Blob is valid
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          this.bannerImages[bannerDetailsSeq] = this.sanitizer.bypassSecurityTrustUrl(url);
        } else {
          console.warn(`Empty image blob for banner ${bannerDetailsSeq}`);
        }

        // ✅ Update UI
        this.isLoadingViewModal = false;
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error(`Error loading banner image ${bannerDetailsSeq}:`, err);
        this.isLoadingViewModal = false;
      }
    });
}
  deleteBanner(bannerDetailsSeq: number): void {
    if (confirm('Are you sure you want to delete this banner?')) {
      this.spinner.show();
      this.appService.deleteBannerById(bannerDetailsSeq)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastr.success('Banner deleted successfully!');
            this.closeViewModal();
            this.closeBannerModal();
            this.loadBanners();
          },
          error: (err) => {
            this.toastr.error(err.message || 'Failed to delete banner');
          },
          complete: () => {
            setTimeout(() => this.spinner.hide(), 1000);
          }
        });
    }
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedBanner = null;
  }

  editBanner(bannerDetailsSeq: number): void {
    const banner = this.banners.find(b => b.bannerDetailsSeq === bannerDetailsSeq);
    if (banner) {
      this.bannerForm.reset();
      this.showBannerModal = true;
      this.bannerForm.patchValue({
        bannerTitle: banner.bannerTitle,
        location: banner.location,
        startDate: banner.startDate,
        endDate: banner.endDate,
        startTime: banner.startTime,
        endTime: banner.endTime,
        bannerImagePath: banner.bannerImagePath,
        shortDescription: banner.shortDescription,
        description: banner.description,
        fileData: banner.fileData || '',
        fileType: banner.fileType || ''
      });
      // Add or update the bannerDetailsSeq control
      if (!this.bannerForm.get('bannerDetailsSeq')) {
        this.bannerForm.addControl('bannerDetailsSeq', this.fb.control(banner.bannerDetailsSeq));
      } else {
        this.bannerForm.get('bannerDetailsSeq')?.setValue(banner.bannerDetailsSeq);
      }
    } else {
      this.toastr.error('Banner not found');
    }
  }

  saveBannerDetails(): void {
    this.spinner.show();

    const payload = {
      bannerTitle: this.bannerForm.get('bannerTitle')?.value,
      location: this.bannerForm.get('location')?.value,
      startDate: this.bannerForm.get('startDate')?.value,
      endDate: this.bannerForm.get('endDate')?.value,
      startTime: this.bannerForm.get('startTime')?.value,
      endTime: this.bannerForm.get('endTime')?.value,
      bannerImagePath: this.bannerForm.get('bannerImagePath')?.value,
      shortDescription: this.bannerForm.get('shortDescription')?.value,
      description: this.bannerForm.get('description')?.value,
      fileData: this.bannerForm.get('fileData')?.value || null,
      fileType: this.bannerForm.get('fileType')?.value || null
    };

    console.log('Saving banner payload:', payload);

    this.appService.saveBannerDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Banner saved successfully!');
          this.bannerForm.reset();
          this.closeBannerModal();
          this.loadBanners();
        },
        error: (error) => {
          this.toastr.error(error.message || 'Something went wrong, please try again');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        }
      });
  }

  updateBannerDetails(): void {
    this.spinner.show();
    
    const payload = {
      bannerDetailsSeq: this.bannerForm.get('bannerDetailsSeq')?.value,
      bannerTitle: this.bannerForm.get('bannerTitle')?.value,
      location: this.bannerForm.get('location')?.value,
      startDate: this.bannerForm.get('startDate')?.value,
      endDate: this.bannerForm.get('endDate')?.value,
      startTime: this.bannerForm.get('startTime')?.value,
      endTime: this.bannerForm.get('endTime')?.value,
      bannerImagePath: this.bannerForm.get('bannerImagePath')?.value,
      shortDescription: this.bannerForm.get('shortDescription')?.value,
      description: this.bannerForm.get('description')?.value,
      fileData: this.bannerForm.get('fileData')?.value || null,
      fileType: this.bannerForm.get('fileType')?.value || null
    };

    console.log('Update Banner Payload:', payload);

    this.appService.updateBannerDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastr.success('Banner updated successfully!');
          this.bannerForm.reset();
          this.closeBannerModal();
          this.loadBanners();
        },
        error: (error) => {
          this.toastr.error(error.message || 'Failed to update banner');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        }
      });
  }

  loadBanners(): void {
    this.spinner.show();
    this.appService.getAllBanners()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.banners = data.sort((a: any, b: any) => b.bannerDetailsSeq - a.bannerDetailsSeq);
          // Load banner images for each banner
          this.banners.forEach(banner => {
            if (banner.bannerDetailsSeq) {
              this.loadBannerImage(banner.bannerDetailsSeq);
            }
          });
        },
        error: (err) => {
          console.error('Error loading banners:', err);
          this.toastr.error('Failed to load banners');
        },
        complete: () => {
          setTimeout(() => this.spinner.hide(), 2000);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
